/**
 * Collections compound tool — list, create, update.
 * Consolidates list-collections, create-collection, update-collection into one tool.
 */

import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod/v3";
import { kernelQuery, kernelMutation, requireWriteAccess, getAgentSessionId, runWithToolContext } from "../client.js";
import { invalidateCollectionCacheForScope } from "../lib/collectionCache.js";
import { invalidateProfileCacheForScope, invalidateHubCacheForScope } from "./smart-capture.js";
import { trackWriteTool } from "../tool-surface.js";
import { thinWrapper, success, failureResult, validationResult, successResult, parseOrFail, type ToolResult } from "../envelope.js";
import { dispatchDiscriminated, type ActionParamSpec } from "../compound-tool.js";
import { handleLabelsList, handleLabelsCreate, handleLabelsUpdate, handleLabelsDelete, handleLabelsApply, handleLabelsRemove } from "./labels.js";

interface CollectionField {
  key: string;
  type: string;
  required?: boolean;
  searchable?: boolean;
  displayHint?: 'hero' | 'badge' | 'meta' | 'section' | 'hidden' | 'inline-meta';
  zone?: 'header' | 'body' | 'meta';
  colorMap?: Record<string, string>;
  accentSource?: boolean;
  iconMap?: Record<string, string>;
  /** BET-196: Content constraints — maximum character length for field values. */
  maxLength?: number;
  /** BET-196: Content constraints — minimum character length for field values. */
  minLength?: number;
}

interface CollectionRecord {
  slug: string;
  name: string;
  description?: string;
  purpose?: string;
  navGroup?: string;
  icon?: string;
  fields: CollectionField[];
}

// WP-484 S2 (§3, Q4): `collections` absorbs `labels` → label-list/label-create/
// label-update/label-delete/label-apply/label-remove — 12 actions, AT the §3
// ceiling, accepted under the two-layer shape + backpressure + served-schema
// assertion (founder-delegated ruling Q4). Legacy `labels` tool stays
// registered alongside (§7 "alongside" mechanic) — both call the SAME
// handleLabels* functions, one business-logic path per action.
const COLLECTIONS_ACTIONS = [
  "list", "create", "update", "describe", "audit", "export",
  "label-list", "label-create", "label-update", "label-delete", "label-apply", "label-remove",
] as const;
type CollectionsAction = (typeof COLLECTIONS_ACTIONS)[number];

const qualityCriterionSchema = z.object({
  field: z.string().max(200).describe("Entry data field key this criterion applies to, e.g. 'description', 'owner'"),
  // WP-480 S1: `max_length` — must mirror the Convex rule union, or the tool rejects a
  // valid criterion before the request ever reaches the server.
  rule: z.enum(['required', 'min_length', 'max_length', 'pattern'])
    .describe("'required': field must be non-empty (blocks accept). 'min_length': minimum string length (warns). 'max_length': maximum string length (warns). 'pattern': regex match (warns)."),
  value: z.string().max(500).optional()
    .describe("For min_length/max_length: the length bound as a string integer. For pattern: the regex string. Unused for 'required'."),
});

const fieldSchema = z.object({
  key: z.string().max(200).describe("Field key, e.g. 'description', 'severity', 'status'"),
  label: z.string().max(200).describe("Display label, e.g. 'Description', 'Severity'"),
  type: z.string().max(50).describe("Field type: 'string', 'select', 'array', 'number', 'boolean'"),
  required: z.boolean().optional().describe("Whether this field is required"),
  options: z.array(z.string().max(200)).max(200).optional().describe("Options for 'select' type fields"),
  searchable: z.boolean().optional().describe("Whether this field is included in full-text search"),
  displayHint: z.enum(['hero', 'badge', 'meta', 'section', 'hidden', 'inline-meta']).optional()
    .describe("V2 rendering hint: how the field should be displayed in Cortex"),
  zone: z.enum(['header', 'body', 'meta']).optional()
    .describe("V2 layout zone: where the field appears in the entry view"),
  colorMap: z.record(z.string().max(50)).optional()
    .describe("V2 value-to-semantic-color mapping, e.g. { critical: 'danger', low: 'success' }"),
  // ENT-61
  accentSource: z.boolean().optional()
    .describe("When true, this field's colorMap value drives the card-level accent styling"),
  // ENT-61
  iconMap: z.record(z.string(), z.string().max(50)).optional()
    .describe("Maps field values to icons (emoji/symbol), prepended to badge text"),
  helpText: z.string().max(2000).optional()
    .describe("Help text shown in editors and describe output"),
  optionDescriptions: z.record(z.string().max(500)).optional()
    .describe("Per-option guidance for select fields"),
  // BET-136
  semanticRole: z.enum(['problem', 'appetite', 'elements', 'architecture', 'done_when', 'risks', 'exclusions']).optional()
    .describe("Semantic role for schema-driven consumers — enables field-key-independent validation and rendering"),
  // BET-196
  maxLength: z.number().optional()
    .describe("Maximum character length for field values. Three-tier resolution: explicit > displayHint > type fallback."),
  // BET-196
  minLength: z.number().optional()
    .describe("Minimum character length for field values. Only explicit — no defaults."),
});

// §3a — the REGISTERED layer: flat ZodObject, action enum + superset of params.
export const collectionsSchema = z.object({
  action: z.enum(COLLECTIONS_ACTIONS).describe(
    "'list': browse all collections. 'create': create a new collection. 'update': update an existing collection. 'describe': full documentation for one collection — fields, option guides, usage guidance, examples. 'audit': health report for all collections — missing classification, icon, displayHint coverage, and field schema gaps. 'export': full system_collection_definitions export with classification metadata (thinkingLayer, classificationPriority, classificationCheck, classificationSignals, governanceRole, governanceFunction, timelineRole, canBeElementOf, descriptionFieldKey). Admin only. " +
      "'label-list'/'label-create'/'label-update'/'label-delete'/'label-apply'/'label-remove': manage workspace labels (absorbs the `labels` tool).",
  ),
  slug: z.string().max(200).optional().describe("URL-safe identifier for create/update, e.g. 'glossary', 'tech-debt'. For label-*: label slug."),
  name: z.string().max(500).optional().describe("Display name for create, or new name for update. For label-create: label display name."),
  description: z.string().max(20000).optional().describe("What this collection is for. For label-create/label-update: what the label means."),
  purpose: z.string().max(2000).optional().describe("Why this collection exists — strategic reason"),
  icon: z.string().max(50).optional().describe("Emoji icon for the collection"),
  navGroup: z.enum(["daily", "strategic", "governance", "reference", "collections"]).optional()
    .describe("Sidebar placement: 'daily', 'strategic', 'governance', 'reference', 'collections'"),
  fields: z.array(fieldSchema).max(200).optional()
    .describe("Field definitions for create, or replacement schema for update (replaces all fields)"),
  // ENT-62
  defaultCanonicalKey: z.string().max(200).optional()
    .describe("The canonical_key entries in this collection default to (e.g. 'decision', 'insight'). Consumers read from collection doc; code map is fallback."),
  // ENT-67
  defaultWorkflowStatus: z.string().max(200).optional()
    .describe("Default workflowStatus for new entries. Must be in validWorkflowStatuses when set (e.g. 'hypothesis' for insights)."),
  // ENT-65
  validWorkflowStatuses: z.array(z.string().max(200)).max(50).optional()
    .describe("The allowed workflowStatus values for entries in this collection. New entries are validated against this list. Empty array means no constraint."),
  // ENT-65, FEAT-200
  classificationCheck: z.string().max(500).optional()
    .describe("LLM decision-tree check for this collection (3–500 chars). Guides the classifier in routing entries here."),
  // ENT-65, FEAT-200
  classificationPriority: z.number().optional()
    .describe("Classifier priority (1–9, lower = higher priority). Used with classificationCheck to order the decision tree."),
  // FEAT-301 Slice 2: quality gate criteria and usage guidance.
  // FEAT-257
  qualityCriteria: z.array(qualityCriterionSchema).max(50).optional()
    .describe("Per-collection accept gate rules. 'required' rule hard-blocks accepts on empty fields; 'min_length'/'pattern' rules warn. Pass an empty array to clear all criteria."),
  usageGuidance: z.string().max(20000).optional()
    .describe("Plain-text guidance shown to agents and users: when to use this collection, when not to, and what makes a good entry."),
  // For label-*
  color: z.string().max(50).optional().describe("For label-create/label-update: hex color, e.g. '#ef4444'."),
  parentSlug: z.string().max(200).optional().describe("For label-create: parent group slug for label hierarchy."),
  isGroup: z.boolean().optional().describe("For label-create/label-update: true if this is a group container, not a taggable label."),
  order: z.number().optional().describe("For label-create/label-update: sort order within its group."),
  entryId: z.string().max(200).optional().describe("For label-apply/label-remove: entry ID."),
});

// §3b — the HANDLER layer: per-action discriminatedUnion.
const collectionsListVariant = z.object({ action: z.literal("list") });
const collectionsDescribeVariant = z.object({ action: z.literal("describe"), slug: z.string().max(200) });
const collectionsCreateVariant = z.object({
  action: z.literal("create"),
  slug: z.string().max(200),
  name: z.string().max(500),
  description: z.string().max(20000).optional(),
  purpose: z.string().max(2000).optional(),
  icon: z.string().max(50).optional(),
  navGroup: z.enum(["daily", "strategic", "governance", "reference", "collections"]).optional(),
  fields: z.array(fieldSchema).min(1),
  defaultCanonicalKey: z.string().max(200).optional(),
  defaultWorkflowStatus: z.string().max(200).optional(),
  validWorkflowStatuses: z.array(z.string().max(200)).optional(),
  classificationCheck: z.string().max(500).optional(),
  classificationPriority: z.number().optional(),
});
const collectionsUpdateVariant = z.object({
  action: z.literal("update"),
  slug: z.string().max(200),
  name: z.string().max(500).optional(),
  description: z.string().max(20000).optional(),
  purpose: z.string().max(2000).optional(),
  icon: z.string().max(50).optional(),
  navGroup: z.enum(["daily", "strategic", "governance", "reference", "collections"]).optional(),
  fields: z.array(fieldSchema).optional(),
  defaultCanonicalKey: z.string().max(200).optional(),
  defaultWorkflowStatus: z.string().max(200).optional(),
  validWorkflowStatuses: z.array(z.string().max(200)).optional(),
  classificationCheck: z.string().max(500).optional(),
  classificationPriority: z.number().optional(),
  qualityCriteria: z.array(qualityCriterionSchema).optional(),
  usageGuidance: z.string().max(20000).optional(),
});
const collectionsAuditVariant = z.object({ action: z.literal("audit") });
const collectionsExportVariant = z.object({ action: z.literal("export") });
const collectionsLabelListVariant = z.object({ action: z.literal("label-list") });
const collectionsLabelCreateVariant = z.object({
  action: z.literal("label-create"),
  slug: z.string().max(200),
  name: z.string().max(500),
  color: z.string().max(50).optional(),
  description: z.string().max(2000).optional(),
  parentSlug: z.string().max(200).optional(),
  isGroup: z.boolean().optional(),
  order: z.number().optional(),
});
const collectionsLabelUpdateVariant = z.object({
  action: z.literal("label-update"),
  slug: z.string().max(200),
  name: z.string().max(500).optional(),
  color: z.string().max(50).optional(),
  description: z.string().max(2000).optional(),
  isGroup: z.boolean().optional(),
  order: z.number().optional(),
});
const collectionsLabelDeleteVariant = z.object({ action: z.literal("label-delete"), slug: z.string().max(200) });
const collectionsLabelApplyVariant = z.object({ action: z.literal("label-apply"), slug: z.string().max(200), entryId: z.string().max(200) });
const collectionsLabelRemoveVariant = z.object({ action: z.literal("label-remove"), slug: z.string().max(200), entryId: z.string().max(200) });

export const collectionsActionUnion = z.discriminatedUnion("action", [
  collectionsListVariant,
  collectionsDescribeVariant,
  collectionsCreateVariant,
  collectionsUpdateVariant,
  collectionsAuditVariant,
  collectionsExportVariant,
  collectionsLabelListVariant,
  collectionsLabelCreateVariant,
  collectionsLabelUpdateVariant,
  collectionsLabelDeleteVariant,
  collectionsLabelApplyVariant,
  collectionsLabelRemoveVariant,
]);

/** §4 backpressure text — one entry per action, never used for registration. */
export const COLLECTIONS_ACTION_SPECS: Record<CollectionsAction, ActionParamSpec> = {
  list: { params: [], description: "Browse all collections; no params." },
  describe: { params: ["slug"], description: "slug is required." },
  create: { params: ["slug", "name", "description", "purpose", "icon", "navGroup", "fields", "defaultCanonicalKey", "defaultWorkflowStatus", "validWorkflowStatuses", "classificationCheck", "classificationPriority"], description: "slug, name, and fields are required." },
  update: { params: ["slug", "name", "description", "purpose", "icon", "navGroup", "fields", "defaultCanonicalKey", "defaultWorkflowStatus", "validWorkflowStatuses", "classificationCheck", "classificationPriority", "qualityCriteria", "usageGuidance"], description: "slug is required; at least one other field must be provided." },
  audit: { params: [], description: "Health report for all collections; no params." },
  export: { params: [], description: "Full system_collection_definitions export; no params. Admin only." },
  "label-list": { params: [], description: "List all labels; no params." },
  "label-create": { params: ["slug", "name", "color", "description", "parentSlug", "isGroup", "order"], description: "slug and name are required." },
  "label-update": { params: ["slug", "name", "color", "description", "isGroup", "order"], description: "slug is required." },
  "label-delete": { params: ["slug"], description: "slug is required." },
  "label-apply": { params: ["slug", "entryId"], description: "slug and entryId are required." },
  "label-remove": { params: ["slug", "entryId"], description: "slug and entryId are required." },
};

export function registerCollectionsTools(server: McpServer): void {
  // §3b handler map — one entry per action, dispatched via dispatchDiscriminated.
  const collectionsHandlers: Record<CollectionsAction, (data: any) => Promise<ToolResult>> = {
    list: () => handleList(),
    describe: (data) => handleDescribe(data.slug),
    create: (data) => handleCreate(
      data.slug, data.name, data.description, data.purpose, data.icon, data.navGroup ?? "collections", data.fields,
      { defaultCanonicalKey: data.defaultCanonicalKey, defaultWorkflowStatus: data.defaultWorkflowStatus, validWorkflowStatuses: data.validWorkflowStatuses, classificationCheck: data.classificationCheck, classificationPriority: data.classificationPriority },
    ),
    update: (data) => {
      const hasChanges = data.name || data.description || data.purpose || data.icon || data.navGroup || data.fields
        || data.defaultCanonicalKey !== undefined || data.defaultWorkflowStatus !== undefined || data.validWorkflowStatuses !== undefined
        || data.classificationCheck !== undefined || data.classificationPriority !== undefined || data.qualityCriteria !== undefined || data.usageGuidance !== undefined;
      if (!hasChanges) {
        return Promise.resolve(validationResult("Provide at least one field to update (name, description, purpose, icon, navGroup, fields, defaultCanonicalKey, defaultWorkflowStatus, validWorkflowStatuses, classificationCheck, classificationPriority, qualityCriteria, or usageGuidance)."));
      }
      return handleUpdate(
        data.slug, data.name, data.description, data.purpose, data.icon, data.navGroup, data.fields,
        { defaultCanonicalKey: data.defaultCanonicalKey, defaultWorkflowStatus: data.defaultWorkflowStatus, validWorkflowStatuses: data.validWorkflowStatuses, classificationCheck: data.classificationCheck, classificationPriority: data.classificationPriority, qualityCriteria: data.qualityCriteria, usageGuidance: data.usageGuidance },
      );
    },
    audit: () => handleAudit(),
    export: () => handleExport(),
    "label-list": () => handleLabelsList(),
    "label-create": (data) => handleLabelsCreate(data.slug, data.name, data.color, data.description, data.parentSlug, data.isGroup, data.order),
    "label-update": (data) => handleLabelsUpdate(data.slug, data.name, data.color, data.description, data.isGroup, data.order),
    "label-delete": (data) => handleLabelsDelete(data.slug),
    "label-apply": (data) => handleLabelsApply(data.slug, data.entryId),
    "label-remove": (data) => handleLabelsRemove(data.slug, data.entryId),
  };

  const tool = server.registerTool(
    "collections",
    {
      title: "Collections",
      description:
        "Manage knowledge collections and workspace labels. Twelve actions:\n\n" +
        "- **list**: Browse all collections — glossary, business rules, tracking events, etc. " +
        "Returns slug, name, description, and field schema. Use before capture to see what exists.\n" +
        "- **describe**: Full documentation for a single collection. Returns field help text, " +
        "option decision guides, usage guidance, examples, and cross-references. Use when you need to " +
        "understand a collection's purpose, field semantics, or option values.\n" +
        "- **create**: Create a new collection. Provide slug, name, and field schema. " +
        "Use when setting up a workspace or tracking a new type of knowledge.\n" +
        "- **update**: Update an existing collection's name, description, purpose, icon, navGroup, or fields. " +
        "Only provide the fields you want to change.\n" +
        "- **audit**: Health report for all workspace collections. Checks classification metadata, icon presence, " +
        "displayHint coverage per field, and field schema gaps vs system definitions. " +
        "Returns total collections, count with issues, and per-collection issue list.\n" +
        "- **export**: Full system_collection_definitions export as JSON. Includes all classification metadata " +
        "(thinkingLayer, classificationPriority, classificationCheck, timelineRole, governanceRole, governanceFunction, etc.). Admin only.\n" +
        "- **label-list/label-create/label-update/label-delete/label-apply/label-remove**: manage workspace " +
        "labels (absorbs the `labels` tool) — list all labels, create/update/delete a label, or apply/remove " +
        "a label on an entry. Labels can be applied to any entry across any collection for cross-domain filtering.",
      inputSchema: collectionsSchema,
      annotations: { readOnlyHint: false, destructiveHint: true, openWorldHint: false },
    },
    thinWrapper(async (args) => {
      const parsed = parseOrFail(collectionsSchema, args);
      if (!parsed.ok) return parsed.result;
      const { action } = parsed.data;

      return runWithToolContext({ tool: "collections", action }, () =>
        dispatchDiscriminated("collections", collectionsActionUnion, parsed.data, COLLECTIONS_ACTION_SPECS, collectionsHandlers),
      );
    }),
  );
  trackWriteTool(tool);
}

interface DescribeCollectionRecord {
  slug: string;
  name: string;
  description?: string;
  purpose?: string;
  usageGuidance?: string;
  navGroup?: string;
  governed?: boolean;
  icon?: string;
  idPrefix?: string;
  stratum?: string;
  /** WP-480 S2: served workflow vocabulary — the discovery path connectors point at (TEN-2219). */
  validWorkflowStatuses?: string[];
  fields: Array<CollectionField & {
    label?: string;
    options?: string[];
    helpText?: string;
    optionDescriptions?: Record<string, string>;
  }>;
  examples?: Array<{ name: string; description: string }>;
  crossReferences?: Array<{ slug: string; relationship: string; guidance: string }>;
}

async function handleDescribe(slug: string) {
  const col = await kernelQuery<DescribeCollectionRecord | null>("chain.getCollection", { slug });

  if (!col) {
    return failureResult(
      `Collection '${slug}' not found.`,
      "NOT_FOUND",
      `No collection with slug '${slug}' in this workspace.`,
      "Use `collections action=list` to see available collections.",
    );
  }

  const sections: string[] = [];

  sections.push(`# ${col.name} (\`${col.slug}\`)`);
  if (col.description) sections.push(col.description);

  const meta = [
    col.governed ? "**Governed**" : null,
    col.navGroup ? `**Nav group:** ${col.navGroup}` : null,
    col.idPrefix ? `**Prefix:** ${col.idPrefix}-*` : null,
    col.stratum ? `**Stratum:** ${col.stratum}` : null,
  ].filter(Boolean).join(" · ");
  if (meta) sections.push(meta);

  if (col.usageGuidance) {
    sections.push(`\n## When to Use\n\n${col.usageGuidance}`);
  } else if (col.purpose) {
    sections.push(`\n## Purpose\n\n${col.purpose}`);
  }

  // WP-480 S2 (FEAT-1359): serve the workflow vocabulary — this is the discovery
  // path for `workflowStatus` values. Collections without validWorkflowStatuses
  // are unconstrained by design (STD-34) and get no section.
  if (col.validWorkflowStatuses && col.validWorkflowStatuses.length > 0) {
    sections.push(
      `\n## Workflow Statuses\n\nValid \`workflowStatus\` values for entries in this collection:\n${col.validWorkflowStatuses.map((s) => `- ${s}`).join("\n")}`,
    );
  }

  if (col.fields.length > 0) {
    const fieldDocs = col.fields.map((f) => {
      const parts: string[] = [];
      const typeInfo = `${f.type}${f.required ? ", required" : ""}`;
      parts.push(`### \`${f.key}\` — ${f.label ?? f.key} (${typeInfo})`);
      if (f.helpText) parts.push(f.helpText);
      if (f.options && f.options.length > 0) {
        const optLines = f.options.map((opt) => {
          const desc = f.optionDescriptions?.[opt];
          return desc ? `  - **${opt}**: ${desc}` : `  - ${opt}`;
        });
        parts.push(`\nOptions:\n${optLines.join("\n")}`);
      }
      return parts.join("\n");
    });
    sections.push(`\n## Fields\n\n${fieldDocs.join("\n\n")}`);
  }

  if (col.examples && col.examples.length > 0) {
    const exList = col.examples.map((ex) => `- **${ex.name}** — ${ex.description}`).join("\n");
    sections.push(`\n## Examples\n\n${exList}`);
  }

  if (col.crossReferences && col.crossReferences.length > 0) {
    const refList = col.crossReferences.map((r) =>
      `- **${r.slug}** (${r.relationship}): ${r.guidance}`
    ).join("\n");
    sections.push(`\n## Related Collections\n\n${refList}`);
  }

  return {
    content: [{ type: "text" as const, text: sections.join("\n\n") }],
    structuredContent: success(
      `Documentation for collection '${col.name}' (${col.slug}).`,
      {
        slug: col.slug,
        name: col.name,
        description: col.description,
        usageGuidance: col.usageGuidance ?? null,
        // WP-480 S2: null = unconstrained by design (STD-34), not "unknown".
        validWorkflowStatuses:
          col.validWorkflowStatuses && col.validWorkflowStatuses.length > 0
            ? col.validWorkflowStatuses
            : null,
        fieldCount: col.fields.length,
        fields: col.fields.map((f) => ({
          key: f.key,
          type: f.type,
          required: f.required,
          helpText: f.helpText ?? null,
          options: f.options ?? null,
          optionDescriptions: f.optionDescriptions ?? null,
        })),
        examples: col.examples ?? [],
        crossReferences: col.crossReferences ?? [],
      },
    ),
  };
}

async function handleList() {
  const collections = await kernelQuery<CollectionRecord[]>("chain.listCollections");

  if (collections.length === 0) {
    return successResult(
      "No collections found in this workspace.",
      "No collections found yet.",
      { collections: [], total: 0 },
      [{ tool: "collections", description: "Create collection", parameters: { action: "create" } }],
    );
  }

  const formatted = collections
    .map((c) => {
      const fieldList = c.fields
        .map((f) => `  - \`${f.key}\` (${f.type}${f.required ? ", required" : ""}${f.searchable ? ", searchable" : ""})`)
        .join("\n");
      const meta = [
        c.description || "_No description_",
        c.purpose ? `**Purpose:** ${c.purpose}` : null,
        c.navGroup ? `**Nav group:** ${c.navGroup}` : null,
      ].filter(Boolean).join("\n");
      return `## ${c.name} (\`${c.slug}\`)\n${meta}\n\n**Fields:**\n${fieldList}`;
    })
    .join("\n\n---\n\n");

  return {
    content: [{ type: "text" as const, text: `# Knowledge Collections (${collections.length})\n\n${formatted}` }],
    structuredContent: success(
      `Found ${collections.length} collections.`,
      {
        collections: collections.map((c) => ({
          slug: c.slug,
          name: c.name,
          description: c.description,
          fieldCount: c.fields.length,
        })),
        total: collections.length,
      },
    ),
  };
}

interface CollectionMetaArgs {
  defaultCanonicalKey?: string;
  defaultWorkflowStatus?: string;
  validWorkflowStatuses?: string[];
  classificationCheck?: string;
  classificationPriority?: number;
  // FEAT-301 Slice 2
  qualityCriteria?: z.infer<typeof qualityCriterionSchema>[];
  usageGuidance?: string;
}

async function handleCreate(
  slug: string,
  name: string,
  description: string | undefined,
  purpose: string | undefined,
  icon: string | undefined,
  navGroup: string,
  fields: z.infer<typeof fieldSchema>[],
  meta: CollectionMetaArgs = {},
) {
  requireWriteAccess();

  try {
    await kernelMutation("chain.createCollection", {
      slug,
      name,
      description,
      purpose,
      icon,
      navGroup,
      fields,
      ...(meta.defaultCanonicalKey !== undefined && { defaultCanonicalKey: meta.defaultCanonicalKey }),
      ...(meta.defaultWorkflowStatus !== undefined && { defaultWorkflowStatus: meta.defaultWorkflowStatus }),
      ...(meta.validWorkflowStatuses !== undefined && { validWorkflowStatuses: meta.validWorkflowStatuses }),
      ...(meta.classificationCheck !== undefined && { classificationCheck: meta.classificationCheck }),
      ...(meta.classificationPriority !== undefined && { classificationPriority: meta.classificationPriority }),
      createdBy: getAgentSessionId() ? `agent:${getAgentSessionId()}` : "mcp",
    });

    // A collection write changes workspace structure — drop this scope's cached
    // shape AND the caches derived from it (profiles, hub-slug set) so the next
    // call (this agent, this workspace) sees it immediately, rather than waiting
    // out the TTL. Scoped, so other tenants are untouched.
    invalidateCollectionCacheForScope();
    invalidateProfileCacheForScope();
    invalidateHubCacheForScope();

    const fieldList = fields
      .map((f) => `  - \`${f.key}\` (${f.type}${f.required ? ", required" : ""}${f.searchable ? ", searchable" : ""})`)
      .join("\n");

    return {
      content: [{
        type: "text" as const,
        text: `# Collection Created: ${name}\n\n` +
          `**Slug:** \`${slug}\`\n` +
          (description ? `**Description:** ${description}\n` : "") +
          (purpose ? `**Purpose:** ${purpose}\n` : "") +
          `**Nav group:** ${navGroup}\n` +
          `\n**Fields:**\n${fieldList}\n\n` +
          `You can now capture entries: \`capture collection="${slug}" name="..." description="..."\``,
      }],
      structuredContent: success(
        `Created collection '${name}' (${slug}) with ${fields.length} fields.`,
        { slug, name, description, purpose, navGroup, fieldCount: fields.length },
        [{ tool: "capture", description: "Capture an entry", parameters: { collection: slug } }],
      ),
    };
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    if (msg.includes("already exists")) {
      return failureResult(
        `Collection '${slug}' already exists. Use collections action=update to modify it, or choose a different slug.`,
        "DUPLICATE",
        `Collection '${slug}' already exists.`,
        "Use collections action=update to modify it.",
        [{ tool: "collections", description: "Update collection", parameters: { action: "update", slug } }],
      );
    }
    const code = (error as { code?: string }).code ?? (error as { data?: { code?: string } }).data?.code;
    if (code === "COLLECTION_PREFIX_TAKEN" || msg.includes("already used by collection")) {
      return failureResult(
        msg,
        "DUPLICATE",
        msg,
        "Choose a different prefix by passing the idPrefix parameter, or let the system derive one from a different slug.",
        [{ tool: "collections", description: "List collections to see taken prefixes", parameters: { action: "list" } }],
      );
    }
    throw error;
  }
}

async function handleUpdate(
  slug: string,
  name: string | undefined,
  description: string | undefined,
  purpose: string | undefined,
  icon: string | undefined,
  navGroup: string | undefined,
  fields: z.infer<typeof fieldSchema>[] | undefined,
  meta: CollectionMetaArgs = {},
) {
  requireWriteAccess();

  await kernelMutation("chain.updateCollection", {
    slug,
    ...(name !== undefined && { name }),
    ...(description !== undefined && { description }),
    ...(purpose !== undefined && { purpose }),
    ...(icon !== undefined && { icon }),
    ...(navGroup !== undefined && { navGroup }),
    ...(fields !== undefined && { fields }),
    ...(meta.defaultCanonicalKey !== undefined && { defaultCanonicalKey: meta.defaultCanonicalKey }),
    ...(meta.defaultWorkflowStatus !== undefined && { defaultWorkflowStatus: meta.defaultWorkflowStatus }),
    ...(meta.validWorkflowStatuses !== undefined && { validWorkflowStatuses: meta.validWorkflowStatuses }),
    ...(meta.classificationCheck !== undefined && { classificationCheck: meta.classificationCheck }),
    ...(meta.classificationPriority !== undefined && { classificationPriority: meta.classificationPriority }),
    ...(meta.qualityCriteria !== undefined && { qualityCriteria: meta.qualityCriteria }),
    ...(meta.usageGuidance !== undefined && { usageGuidance: meta.usageGuidance }),
  });

  // A collection write changes workspace structure — drop this scope's cached
  // shape AND the caches derived from it (profiles, hub-slug set) so the next
  // call (this agent, this workspace) sees it immediately, rather than waiting
  // out the TTL. Scoped, so other tenants are untouched.
  invalidateCollectionCacheForScope();
  invalidateProfileCacheForScope();
  invalidateHubCacheForScope();

  const changes = [name && "name", description && "description", purpose && "purpose", icon && "icon", navGroup && "navGroup", fields && "fields", meta.defaultCanonicalKey !== undefined && "defaultCanonicalKey", meta.defaultWorkflowStatus !== undefined && "defaultWorkflowStatus", meta.validWorkflowStatuses !== undefined && "validWorkflowStatuses", meta.classificationCheck !== undefined && "classificationCheck", meta.classificationPriority !== undefined && "classificationPriority", meta.qualityCriteria !== undefined && "qualityCriteria", meta.usageGuidance !== undefined && "usageGuidance"]
    .filter(Boolean).join(", ");

  return {
    content: [{
      type: "text" as const,
      text: `# Collection Updated: \`${slug}\`\n\nChanged: ${changes || "no changes"}.\n\n` +
        `Use \`collections action=list\` to verify the result.`,
    }],
    structuredContent: success(
      `Updated collection '${slug}'. Changed: ${changes || "no changes"}.`,
      { slug, changes },
    ),
  };
}

interface AuditIssue {
  code: string;
  message: string;
  fieldKey?: string;
}

interface AuditCollectionResult {
  slug: string;
  name: string;
  icon?: string;
  fieldCount: number;
  issues: AuditIssue[];
}

interface AuditCollectionsData {
  total: number;
  healthy: number;
  withIssues: number;
  collections: AuditCollectionResult[];
}

async function handleAudit() {
  const data = await kernelQuery<AuditCollectionsData>("chain.auditCollections");

  const lines: string[] = [];
  lines.push(`# Collection Audit Report`);
  lines.push(`\n**Total:** ${data.total} · **Healthy:** ${data.healthy} · **With issues:** ${data.withIssues}`);

  if (data.withIssues === 0) {
    lines.push(`\nAll collections are healthy.`);
  } else {
    const problematic = data.collections.filter((c) => c.issues.length > 0);
    lines.push(`\n## Collections With Issues\n`);
    for (const col of problematic) {
      const icon = col.icon ?? "—";
      lines.push(`### ${icon} ${col.name} (\`${col.slug}\`) — ${col.issues.length} issue(s)`);
      for (const issue of col.issues) {
        lines.push(`- **[${issue.code}]** ${issue.message}`);
      }
      lines.push("");
    }
  }

  const healthyCollections = data.collections.filter((c) => c.issues.length === 0);
  if (healthyCollections.length > 0) {
    lines.push(`\n## Healthy Collections\n`);
    lines.push(healthyCollections.map((c) => `- ${c.icon ?? "—"} \`${c.slug}\``).join("\n"));
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Audit complete. ${data.total} collections: ${data.healthy} healthy, ${data.withIssues} with issues.`,
      data,
    ),
  };
}

/**
 * BET-288 Slice 5: Export all system_collection_definitions with full classification metadata.
 * Returns the complete definition for every system collection, including fields not in "describe":
 * thinkingLayer, classificationPriority, classificationCheck, classificationSignals,
 * governanceRole, governanceFunction, timelineRole, canBeElementOf, descriptionFieldKey.
 * Admin only (enforced by the HTTP route).
 */
async function handleExport() {
  const definitions = await kernelQuery<Record<string, unknown>[]>("chain.exportDefinitions");

  return {
    content: [{ type: "text" as const, text: JSON.stringify(definitions, null, 2) }],
    structuredContent: success(
      `Exported ${definitions.length} system collection definitions with full classification metadata.`,
      { definitions, total: definitions.length },
    ),
  };
}
